import { Link } from "react-router-dom";
import { useScrollFadeIn, staggerStyle } from "@/hooks/useScrollFadeIn";

type Step = {
  num: string;
  title: string;
  desc: string;
  icon: string;
  link?: string;
  linkLabel?: string;
  linkInternal?: boolean;
};

const steps: Step[] = [
  {
    num: "01",
    title: "カレンダー確認",
    desc: "lit.linkのカレンダーより、ご希望の日程をご確認ください。",
    icon: "ri-calendar-check-line",
    link: "https://lit.link/hatomugicha91",
    linkLabel: "カレンダーを確認する",
  },
  {
    num: "02",
    title: "DM またはメールから予約",
    desc: "XのDMまたはメールに必要事項を記入の上ご連絡ください。",
    icon: "ri-message-3-line",
    link: "/reservation",
    linkLabel: "予約フォームへ",
    linkInternal: true,
  },
  {
    num: "03",
    title: "むぎ茶から返信",
    desc: "ご返信いたします。初回の方はカウンセリングがございます。",
    icon: "ri-reply-line",
  },
  {
    num: "04",
    title: "前日確認",
    desc: "前日に確認のご連絡をいたします。",
    icon: "ri-notification-3-line",
  },
  {
    num: "05",
    title: "プレイ当日",
    desc: "ホテル名・部屋番号をお知らせください。\nお待ちしております。",
    icon: "ri-hotel-line",
  },
];

export default function HowToBookSection() {
  const { ref: headRef, style: headStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: stepsRef, visible } = useScrollFadeIn<HTMLDivElement>({ delay: 0.05 });
  const { ref: mobileStepsRef, visible: mobileVisible } = useScrollFadeIn<HTMLDivElement>({ delay: 0.05 });

  return (
    <section id="how-to-book" className="py-20 md:py-14 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div ref={headRef} style={headStyle} className="text-center mb-5 md:mb-8">
          <p className="text-xs tracking-[0.4em] text-amber-700 uppercase mb-2 md:mb-3">How to Book</p>
          <h2
            className="text-3xl md:text-4xl font-normal text-stone-800 tracking-widest"
          >
            ご利用の流れ
          </h2>
          <div className="w-12 h-0.5 bg-amber-400 mx-auto mt-4 md:mt-5"></div>
        </div>

        {/* Desktop: zigzag timeline */}
        <div ref={stepsRef} className="relative hidden md:block">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-stone-200 -translate-x-1/2"></div>
          <div className="space-y-4">
            {steps.map((step, i) => (
              <div key={i} style={staggerStyle(visible, i, 0.05, 0.1)} className={`flex items-center ${i % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}>
                <div className={`flex-1 ${i % 2 === 0 ? "pr-10 text-right" : "pl-10"}`}>
                  <div className={`relative p-4 inline-block w-full max-w-sm overflow-hidden
                    ${i === 4
                      ? "bg-amber-50 border border-amber-300"
                      : "bg-stone-50 border border-stone-200"}
                    ${i % 2 === 0 ? "ml-auto" : ""}`}>
                    {i === 4 && (
                      <span className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 via-amber-300 to-transparent" />
                    )}
                    <p className={`text-xs tracking-[0.3em] mb-1 ${i === 4 ? "text-amber-700 font-semibold" : "text-amber-600"}`}>{step.num}</p>
                    <h3 className={`text-sm font-medium mb-1 tracking-wider ${i === 4 ? "text-amber-800" : "text-stone-700"}`}>{step.title}</h3>
                    <p className={`text-xs leading-relaxed whitespace-pre-line ${i === 4 ? "text-amber-700" : "text-stone-500"}`}>{step.desc}</p>
                    {step.link && (
                      step.linkInternal ? (
                        <Link
                          to={step.link}
                          className="inline-flex items-center gap-1.5 mt-2 text-xs text-amber-700 border border-amber-400 px-3 py-1.5 hover:bg-amber-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-mail-line text-xs"></i>
                          {step.linkLabel}
                        </Link>
                      ) : (
                        <a
                          href={step.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-2 text-xs text-amber-700 border border-amber-400 px-3 py-1.5 hover:bg-amber-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line text-xs"></i>
                          {step.linkLabel}
                        </a>
                      )
                    )}
                    {i === 4 && (
                      <span className="absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-l from-amber-400 via-amber-300 to-transparent" />
                    )}
                  </div>
                </div>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 z-10
                  ${i === 4
                    ? "bg-amber-400 border border-amber-400"
                    : "bg-white border border-stone-200"}`}>
                  <i className={`${step.icon} text-sm ${i === 4 ? "text-white" : "text-amber-600"}`}></i>
                </div>
                <div className="flex-1"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button - PC only */}
        <div className="hidden md:flex justify-center mt-8">
          <Link
            to="/reservation"
            className="inline-flex items-center gap-3 px-10 py-4 bg-stone-800 text-white text-sm tracking-[0.3em] hover:bg-stone-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-line text-sm"></i>
            ご予約はこちら
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>

        {/* Mobile: vertical list */}
        <div ref={mobileStepsRef} className="md:hidden space-y-4">
          {steps.map((step, i) => (
            <div key={i} style={staggerStyle(mobileVisible, i, 0.05, 0.1)} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-white border border-stone-200 flex items-center justify-center flex-shrink-0">
                <i className={`${step.icon} text-amber-600 text-sm`}></i>
              </div>
              <div className="flex-1 pb-4 border-b border-stone-100">
                <p className="text-base tracking-[0.2em] text-amber-600 mb-1">{step.num}</p>
                <h3 className="text-base font-medium text-stone-700 mb-1 whitespace-nowrap">{step.title}</h3>
                <p className={`text-base text-stone-500 leading-relaxed ${i === 3 ? "whitespace-nowrap" : ""}`}>{step.desc}</p>
                {step.link && (
                  step.linkInternal ? (
                    <Link
                      to={step.link}
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-amber-700 border border-amber-400 px-3 py-1.5 hover:bg-amber-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-line text-xs"></i>
                      {step.linkLabel}
                    </Link>
                  ) : (
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-amber-700 border border-amber-400 px-3 py-1.5 hover:bg-amber-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      {step.linkLabel}
                    </a>
                  )
                )}
                {i === 4 && (
                  <div className="flex justify-center mt-6 -ml-14">
                    <Link
                      to="/reservation"
                      className="inline-flex items-center gap-3 px-8 py-3 bg-stone-800 text-white text-sm tracking-[0.3em] hover:bg-stone-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-calendar-line text-sm"></i>
                      ご予約はこちら
                      <i className="ri-arrow-right-line text-sm"></i>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
